import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

export interface NavContextMenuState {
  x: number;
  y: number;
  path: string;
  label: string;
}

interface NavContextMenuProps {
  menu: NavContextMenuState | null;
  onClose: () => void;
  onOpenInNewTab: (path: string) => void;
  onOpenLink: (path: string) => void;
  onCopyLink: (path: string) => void;
}

export function NavContextMenu({
  menu,
  onClose,
  onOpenInNewTab,
  onOpenLink,
  onCopyLink,
}: NavContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menu) return;

    const onPointerDown = (event: MouseEvent) => {
      if (ref.current?.contains(event.target as Node)) return;
      onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [menu, onClose]);

  if (!menu) return null;

  const left = Math.min(menu.x, globalThis.innerWidth - 200);
  const top = Math.min(menu.y, globalThis.innerHeight - 140);

  return createPortal(
    <div
      ref={ref}
      className="fixed z-[10050] min-w-[180px] overflow-hidden rounded-xl border border-line bg-surface py-1 shadow-xl"
      style={{ left, top }}
      role="menu"
    >
      <button
        type="button"
        role="menuitem"
        className="block w-full px-3 py-2 text-left text-sm text-foreground hover:bg-hover"
        onClick={() => {
          onOpenInNewTab(menu.path);
          onClose();
        }}
      >
        Open in new tab
      </button>
      <button
        type="button"
        role="menuitem"
        className="block w-full px-3 py-2 text-left text-sm text-foreground hover:bg-hover"
        onClick={() => {
          onOpenLink(menu.path);
          onClose();
        }}
      >
        Open link
      </button>
      <div className="my-1 border-t border-line" />
      <button
        type="button"
        role="menuitem"
        className="block w-full px-3 py-2 text-left text-sm text-foreground hover:bg-hover"
        onClick={() => {
          onCopyLink(menu.path);
          onClose();
        }}
      >
        Copy link address
      </button>
    </div>,
    document.body,
  );
}
